import { useEffect, useState } from 'react';

// eslint-disable-next-line no-restricted-imports
import { Dialog as MuiDialog } from '@mui/material';
import type { DialogProps } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { IconButton } from '../IconButton';
import styles from './Dialog.module.scss';
import { uiIcons } from '@/constants/uiIcons';

interface Props extends Omit<DialogProps, 'open'> {
  open?: boolean;
  closeButtonTooltip?: string;
}

export const Dialog = ({
  open = true,
  onClose,
  closeButtonTooltip,
  children,
  slotProps,
  ...props
}: Props) => {
  const [isOpen, setIsOpen] = useState(open);
  const { t } = useTranslation('ui');

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleClose: NonNullable<DialogProps['onClose']> = (event, reason) => {
    setIsOpen(false);
    onClose?.(event, reason);
  };

  return (
    <MuiDialog
      open={isOpen}
      onClose={handleClose}
      slotProps={{
        ...slotProps,
        paper: {
          ...slotProps?.paper,
          className: cx(
            styles.Paper,
            (slotProps?.paper as { className?: string })?.className,
          ),
        },
      }}
      {...props}
    >
      <IconButton
        data-automation-id="dialog-close-button"
        tooltip={closeButtonTooltip ?? t('dialog.CloseButtonTooltip')}
        iconName={uiIcons.actions.close}
        size="small"
        iconProps={{ weight: 500 }}
        onClick={(e) => handleClose(e, 'backdropClick')} // Treat close button click as backdrop click for consistent onClose reason
        className={styles.CloseButton}
      />
      {children}
    </MuiDialog>
  );
};
